import { useState, FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.tsx';
import './Login.css';

function LogoF() {
  return (
    <svg width="72" height="72" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
      <rect width="80" height="80" rx="18" fill="#0f2a50"/>
      <circle cx="40" cy="36" r="24" fill="none" stroke="#1e3a6e" strokeWidth="8"/>
      <circle cx="40" cy="36" r="24" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeDasharray="28 122" strokeDashoffset="-8"/>
      <circle cx="40" cy="36" r="24" fill="none" stroke="#059669" strokeWidth="2.5" strokeDasharray="20 130" strokeDashoffset="-40"/>
      <circle cx="40" cy="36" r="24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeDasharray="14 136" strokeDashoffset="-64"/>
      <rect x="28" y="24" width="24" height="24" rx="3" fill="#0f2a50"/>
      <rect x="32" y="28" width="6" height="6" rx="1" fill="#3b82f6"/>
      <rect x="42" y="28" width="6" height="6" rx="1" fill="#059669"/>
      <rect x="32" y="38" width="6" height="6" rx="1" fill="#f59e0b"/>
      <rect x="42" y="38" width="6" height="6" rx="1" fill="#3b82f6" opacity="0.5"/>
      <circle cx="40" cy="66" r="3" fill="#3b82f6"/>
      <line x1="40" y1="60" x2="40" y2="63" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function SplashIllustration() {
  return (
    <svg width="480" height="360" viewBox="0 0 480 360" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Dashboard window */}
      <rect x="20" y="20" width="440" height="320" rx="12" fill="#0d1526" stroke="rgba(59,130,246,0.2)" strokeWidth="1" />
      {/* Title bar */}
      <rect x="20" y="20" width="440" height="36" rx="12" fill="#111c35" />
      <rect x="20" y="44" width="440" height="12" fill="#111c35" />
      <circle cx="42" cy="38" r="5" fill="#ef4444" fillOpacity="0.7" />
      <circle cx="58" cy="38" r="5" fill="#f59e0b" fillOpacity="0.7" />
      <circle cx="74" cy="38" r="5" fill="#22c55e" fillOpacity="0.7" />
      <rect x="180" y="33" width="120" height="10" rx="5" fill="rgba(59,130,246,0.15)" />

      {/* Sidebar */}
      <rect x="20" y="56" width="72" height="284" fill="#0a1220" />
      <rect x="28" y="72" width="56" height="8" rx="4" fill="rgba(59,130,246,0.4)" />
      <rect x="28" y="90" width="40" height="6" rx="3" fill="rgba(255,255,255,0.1)" />
      <rect x="28" y="106" width="48" height="6" rx="3" fill="rgba(255,255,255,0.07)" />
      <rect x="28" y="122" width="44" height="6" rx="3" fill="rgba(255,255,255,0.07)" />
      <rect x="28" y="138" width="50" height="6" rx="3" fill="rgba(255,255,255,0.07)" />
      <rect x="28" y="154" width="38" height="6" rx="3" fill="rgba(255,255,255,0.07)" />

      {/* KPI cards row */}
      <rect x="104" y="64" width="90" height="52" rx="8" fill="#111c35" stroke="rgba(59,130,246,0.15)" strokeWidth="1" />
      <rect x="112" y="72" width="32" height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />
      <rect x="112" y="83" width="50" height="10" rx="4" fill="#3b82f6" fillOpacity="0.85" />
      <rect x="112" y="99" width="24" height="4" rx="2" fill="rgba(34,197,94,0.5)" />

      <rect x="202" y="64" width="90" height="52" rx="8" fill="#111c35" stroke="rgba(59,130,246,0.15)" strokeWidth="1" />
      <rect x="210" y="72" width="36" height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />
      <rect x="210" y="83" width="46" height="10" rx="4" fill="#10b981" fillOpacity="0.8" />
      <rect x="210" y="99" width="28" height="4" rx="2" fill="rgba(59,130,246,0.4)" />

      <rect x="300" y="64" width="90" height="52" rx="8" fill="#111c35" stroke="rgba(59,130,246,0.15)" strokeWidth="1" />
      <rect x="308" y="72" width="30" height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />
      <rect x="308" y="83" width="52" height="10" rx="4" fill="#8b5cf6" fillOpacity="0.8" />
      <rect x="308" y="99" width="22" height="4" rx="2" fill="rgba(245,158,11,0.5)" />

      <rect x="398" y="64" width="52" height="52" rx="8" fill="#111c35" stroke="rgba(59,130,246,0.15)" strokeWidth="1" />
      <rect x="406" y="72" width="36" height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />
      <rect x="406" y="83" width="28" height="10" rx="4" fill="#f59e0b" fillOpacity="0.8" />

      {/* Chart area */}
      <rect x="104" y="126" width="188" height="120" rx="8" fill="#111c35" stroke="rgba(59,130,246,0.12)" strokeWidth="1" />
      <rect x="112" y="134" width="60" height="6" rx="3" fill="rgba(255,255,255,0.18)" />
      {/* Bar chart */}
      <rect x="116" y="210" width="18" height="26" rx="3" fill="rgba(59,130,246,0.5)" />
      <rect x="140" y="198" width="18" height="38" rx="3" fill="#3b82f6" fillOpacity="0.8" />
      <rect x="164" y="204" width="18" height="32" rx="3" fill="rgba(59,130,246,0.5)" />
      <rect x="188" y="192" width="18" height="44" rx="3" fill="#3b82f6" />
      <rect x="212" y="201" width="18" height="35" rx="3" fill="rgba(59,130,246,0.6)" />
      <rect x="236" y="196" width="18" height="40" rx="3" fill="#3b82f6" fillOpacity="0.75" />
      {/* x axis */}
      <line x1="112" y1="237" x2="284" y2="237" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

      {/* Right panels */}
      <rect x="300" y="126" width="150" height="56" rx="8" fill="#111c35" stroke="rgba(59,130,246,0.12)" strokeWidth="1" />
      <rect x="308" y="134" width="50" height="5" rx="2.5" fill="rgba(255,255,255,0.18)" />
      <rect x="308" y="147" width="134" height="5" rx="2.5" fill="rgba(255,255,255,0.07)" />
      <rect x="308" y="158" width="100" height="5" rx="2.5" fill="rgba(255,255,255,0.07)" />
      <rect x="308" y="169" width="118" height="5" rx="2.5" fill="rgba(255,255,255,0.07)" />

      <rect x="300" y="190" width="150" height="56" rx="8" fill="#111c35" stroke="rgba(34,197,94,0.15)" strokeWidth="1" />
      <rect x="308" y="198" width="44" height="5" rx="2.5" fill="rgba(255,255,255,0.18)" />
      <rect x="308" y="211" width="120" height="5" rx="2.5" fill="rgba(255,255,255,0.07)" />
      <rect x="308" y="222" width="90" height="5" rx="2.5" fill="rgba(255,255,255,0.07)" />
      <rect x="308" y="233" width="110" height="5" rx="2.5" fill="rgba(255,255,255,0.07)" />

      {/* Bottom row */}
      <rect x="104" y="256" width="346" height="36" rx="8" fill="#111c35" stroke="rgba(59,130,246,0.1)" strokeWidth="1" />
      <rect x="112" y="264" width="60" height="5" rx="2.5" fill="rgba(255,255,255,0.15)" />
      <rect x="112" y="274" width="160" height="4" rx="2" fill="rgba(255,255,255,0.06)" />
      <rect x="360" y="264" width="70" height="14" rx="5" fill="rgba(59,130,246,0.25)" />
      <rect x="366" y="268" width="58" height="6" rx="3" fill="rgba(59,130,246,0.5)" />

      {/* Floating glow dots */}
      <circle cx="104" cy="126" r="3" fill="#3b82f6" fillOpacity="0.6" />
      <circle cx="292" cy="126" r="3" fill="#10b981" fillOpacity="0.6" />
      <circle cx="300" cy="246" r="3" fill="#8b5cf6" fillOpacity="0.6" />
    </svg>
  );
}

const LeftSplash = () => (
  <div className="login-splash-inner">
    <SplashIllustration />
    <p className="login-splash-tagline">SECURE · HIPAA COMPLIANT · CLOUD BASED</p>
  </div>
);

const RightSplash = () => (
  <div style={{ width: '100%', minHeight: '100vh', background: '#070e1c', position: 'relative', overflow: 'hidden' }}>
    <svg width="100%" height="100%" viewBox="0 0 680 520" preserveAspectRatio="xMidYMid meet" style={{ display: 'block', minHeight: '100vh' }} xmlns="http://www.w3.org/2000/svg">
      <rect width="680" height="520" fill="#070e1c"/>
      <circle cx="580" cy="-20" r="180" fill="#1a3a6e" opacity="0.3"/>
      <circle cx="100" cy="540" r="160" fill="#0f2a50" opacity="0.25"/>
      <line x1="0" y1="80" x2="680" y2="80" stroke="#1e3a5f" strokeWidth="0.5"/>
      <line x1="0" y1="160" x2="680" y2="160" stroke="#1e3a5f" strokeWidth="0.5"/>
      <line x1="0" y1="240" x2="680" y2="240" stroke="#1e3a5f" strokeWidth="0.5"/>
      <line x1="0" y1="320" x2="680" y2="320" stroke="#1e3a5f" strokeWidth="0.5"/>
      <line x1="0" y1="400" x2="680" y2="400" stroke="#1e3a5f" strokeWidth="0.5"/>
      <line x1="80" y1="0" x2="80" y2="520" stroke="#1e3a5f" strokeWidth="0.5"/>
      <line x1="200" y1="0" x2="200" y2="520" stroke="#1e3a5f" strokeWidth="0.5"/>
      <line x1="320" y1="0" x2="320" y2="520" stroke="#1e3a5f" strokeWidth="0.5"/>
      <line x1="440" y1="0" x2="440" y2="520" stroke="#1e3a5f" strokeWidth="0.5"/>
      <line x1="560" y1="0" x2="560" y2="520" stroke="#1e3a5f" strokeWidth="0.5"/>
      <g transform="translate(60, 40)">
        <rect x="0" y="0" width="150" height="110" rx="8" fill="#0d1f3c" stroke="#1e3a5f" strokeWidth="1"/>
        <rect x="10" y="12" width="130" height="8" rx="2" fill="#1e3a5f"/>
        <rect x="10" y="26" width="90" height="6" rx="2" fill="#162d52"/>
        <rect x="10" y="38" width="110" height="6" rx="2" fill="#162d52"/>
        <rect x="10" y="50" width="70" height="6" rx="2" fill="#162d52"/>
        <rect x="10" y="66" width="30" height="22" rx="4" fill="#1d4ed8" opacity="0.7"/>
        <rect x="46" y="66" width="30" height="22" rx="4" fill="#065f46" opacity="0.7"/>
        <rect x="82" y="66" width="30" height="22" rx="4" fill="#92400e" opacity="0.6"/>
        <rect x="118" y="66" width="22" height="22" rx="4" fill="#7c3aed" opacity="0.5"/>
        <rect x="0" y="120" width="150" height="75" rx="8" fill="#0d1f3c" stroke="#1e3a5f" strokeWidth="1"/>
        <rect x="10" y="132" width="60" height="6" rx="2" fill="#1e3a5f"/>
        <rect x="10" y="145" width="130" height="2" rx="1" fill="#1e3a5f"/>
        <rect x="10" y="150" width="104" height="2" rx="1" fill="#1d4ed8" opacity="0.8"/>
        <rect x="10" y="158" width="130" height="2" rx="1" fill="#1e3a5f"/>
        <rect x="10" y="163" width="65" height="2" rx="1" fill="#065f46" opacity="0.8"/>
        <rect x="10" y="171" width="130" height="2" rx="1" fill="#1e3a5f"/>
        <rect x="10" y="176" width="117" height="2" rx="1" fill="#92400e" opacity="0.7"/>
      </g>
      <g transform="translate(230, 30)">
        <rect x="0" y="0" width="200" height="110" rx="8" fill="#0d1f3c" stroke="#3b82f6" strokeWidth="1" opacity="0.9"/>
        <rect x="12" y="12" width="44" height="44" rx="6" fill="#1d4ed8" opacity="0.8"/>
        <path d="M22 34 L32 24 L42 30 L54 18" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="22" cy="34" r="2.5" fill="#60a5fa"/>
        <circle cx="32" cy="24" r="2.5" fill="#60a5fa"/>
        <circle cx="42" cy="30" r="2.5" fill="#60a5fa"/>
        <circle cx="54" cy="18" r="2.5" fill="#60a5fa"/>
        <rect x="66" y="12" width="122" height="10" rx="3" fill="#1e3a5f"/>
        <rect x="66" y="28" width="80" height="8" rx="2" fill="#1e3a5f"/>
        <rect x="66" y="42" width="100" height="8" rx="2" fill="#1e3a5f"/>
        <rect x="12" y="72" width="176" height="26" rx="4" fill="#0f2a50"/>
        <rect x="18" y="78" width="40" height="5" rx="2" fill="#1d4ed8" opacity="0.6"/>
        <rect x="78" y="78" width="40" height="5" rx="2" fill="#065f46" opacity="0.6"/>
        <rect x="138" y="78" width="40" height="5" rx="2" fill="#92400e" opacity="0.6"/>
        <rect x="0" y="120" width="96" height="95" rx="8" fill="#0d1f3c" stroke="#1e3a5f" strokeWidth="1"/>
        <rect x="10" y="132" width="76" height="8" rx="2" fill="#1e3a5f"/>
        <rect x="10" y="148" width="32" height="32" rx="4" fill="#065f46" opacity="0.6"/>
        <rect x="52" y="148" width="32" height="32" rx="4" fill="#1d4ed8" opacity="0.5"/>
        <rect x="10" y="186" width="32" height="20" rx="3" fill="#92400e" opacity="0.5"/>
        <rect x="52" y="186" width="32" height="20" rx="3" fill="#1e3a5f"/>
        <rect x="104" y="120" width="96" height="95" rx="8" fill="#0d1f3c" stroke="#1e3a5f" strokeWidth="1"/>
        <rect x="114" y="132" width="76" height="8" rx="2" fill="#1e3a5f"/>
        <rect x="114" y="148" width="76" height="7" rx="2" fill="#162d52"/>
        <rect x="114" y="162" width="56" height="7" rx="2" fill="#162d52"/>
        <rect x="114" y="176" width="66" height="7" rx="2" fill="#162d52"/>
        <rect x="114" y="190" width="30" height="16" rx="3" fill="#1d4ed8" opacity="0.7"/>
      </g>
      <g transform="translate(460, 50)">
        <rect x="0" y="0" width="72" height="72" rx="8" fill="#0d1f3c" stroke="#1e3a5f" strokeWidth="1"/>
        <rect x="8" y="10" width="56" height="7" rx="2" fill="#1e3a5f"/>
        <rect x="8" y="24" width="56" height="30" rx="4" fill="#0f2a50"/>
        <rect x="12" y="28" width="14" height="22" rx="2" fill="#1d4ed8" opacity="0.8"/>
        <rect x="30" y="33" width="14" height="17" rx="2" fill="#065f46" opacity="0.7"/>
        <rect x="48" y="36" width="12" height="14" rx="2" fill="#92400e" opacity="0.6"/>
        <rect x="8" y="58" width="28" height="7" rx="2" fill="#1e3a5f"/>
        <rect x="80" y="0" width="72" height="72" rx="8" fill="#0d1f3c" stroke="#1e3a5f" strokeWidth="1"/>
        <circle cx="116" cy="28" r="14" fill="#0f2a50" stroke="#1d4ed8" strokeWidth="1.5" opacity="0.8"/>
        <path d="M109 28 L115 34 L125 22" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round"/>
        <rect x="88" y="48" width="56" height="6" rx="2" fill="#1e3a5f"/>
        <rect x="88" y="58" width="40" height="6" rx="2" fill="#065f46" opacity="0.6"/>
        <rect x="0" y="82" width="152" height="65" rx="8" fill="#0d1f3c" stroke="#1e3a5f" strokeWidth="1"/>
        <rect x="10" y="94" width="64" height="7" rx="2" fill="#1e3a5f"/>
        <rect x="10" y="108" width="132" height="1" fill="#1e3a5f"/>
        <rect x="10" y="114" width="100" height="6" rx="2" fill="#162d52"/>
        <rect x="10" y="126" width="114" height="6" rx="2" fill="#162d52"/>
        <rect x="0" y="158" width="72" height="68" rx="8" fill="#0d1f3c" stroke="#1e3a5f" strokeWidth="1"/>
        <rect x="8" y="168" width="56" height="7" rx="2" fill="#1e3a5f"/>
        <circle cx="36" cy="198" r="16" fill="none" stroke="#1d4ed8" strokeWidth="2" opacity="0.7"/>
        <path d="M36 182 L36 198 L48 198" fill="none" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round"/>
        <rect x="80" y="158" width="72" height="68" rx="8" fill="#0d1f3c" stroke="#1e3a5f" strokeWidth="1"/>
        <rect x="88" y="168" width="56" height="7" rx="2" fill="#1e3a5f"/>
        <rect x="88" y="184" width="18" height="18" rx="2" fill="#1e3a5f"/>
        <rect x="110" y="184" width="18" height="18" rx="2" fill="#1d4ed8" opacity="0.6"/>
        <rect x="132" y="184" width="12" height="18" rx="2" fill="#1e3a5f"/>
        <rect x="88" y="208" width="56" height="10" rx="2" fill="#1d4ed8" opacity="0.3"/>
      </g>
      <g transform="translate(80, 380)">
        <rect x="0" y="0" width="520" height="110" rx="12" fill="#091020" stroke="#1e3a5f" strokeWidth="0.5" opacity="0.95"/>
        <rect x="20" y="22" width="44" height="44" rx="10" fill="#1d4ed8"/>
        <text x="42" y="50" textAnchor="middle" fontFamily="Arial,sans-serif" fontSize="24" fontWeight="700" fill="white">P</text>
        <text x="78" y="44" fontFamily="Arial,sans-serif" fontSize="26" fontWeight="700" fill="white">Pro<tspan fill="#3b82f6">Q</tspan>ure</text>
        <text x="78" y="62" fontFamily="Arial,sans-serif" fontSize="11" fill="#4a7aaa" letterSpacing="2">AI-POWERED FACILITY MANAGEMENT</text>
        <line x1="20" y1="78" x2="500" y2="78" stroke="#1e3a5f" strokeWidth="0.5"/>
        <text x="20" y="96" fontFamily="Arial,sans-serif" fontSize="11" fill="#2d5a8a" letterSpacing="1">SMART · MODERN · EFFICIENT</text>
      </g>
      <circle cx="80" cy="200" r="3" fill="#3b82f6" opacity="0.6"/>
      <circle cx="200" cy="80" r="2" fill="#3b82f6" opacity="0.4"/>
      <circle cx="440" cy="340" r="2.5" fill="#3b82f6" opacity="0.5"/>
      <circle cx="560" cy="160" r="2" fill="#3b82f6" opacity="0.4"/>
    </svg>
  </div>
);

export default function LoginPage() {
  const { user, login, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('expired') === '1';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForgotPw, setShowForgotPw] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  if (isLoading) {
    return <div className="loading-screen"><div className="loading-spinner" /></div>;
  }

  if (user && !error) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await login(username.trim(), password);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Invalid username or password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', width: '100vw', background: '#070e1c' }}>

      {/* Left splash */}
      <div className="login-splash" style={{ flex: 1, minHeight: '100vh', overflow: 'hidden' }}>
        <LeftSplash />
      </div>

      {/* Login panel — fixed width center */}
      <div style={{ width: 420, minWidth: 420, minHeight: '100vh', background: '#0d1526', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', flexShrink: 0 }}>
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo" style={{ marginBottom: 12 }}>
              <LogoF />
            </div>
            <h1 className="login-title">
              <span style={{ color: 'white', fontWeight: 700 }}>Pro</span><span style={{ color: '#3b82f6', fontWeight: 700 }}>Q</span><span style={{ color: 'white', fontWeight: 700 }}>ure</span>
            </h1>
            <p className="login-subtitle">AI-Powered Facility Management</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label" htmlFor="username">Username or Email</label>
              <input
                id="username"
                type="text"
                className="form-input"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">Password</label>
              <div className="password-field">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && <div className="login-error">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary login-submit"
              disabled={submitting || !username || !password}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <button
            type="button"
            style={{ display: 'block', textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', background: 'none', border: 'none', textDecoration: 'underline', width: '100%' }}
            onClick={() => setShowForgotPw((s) => !s)}
          >
            Forgot password?
          </button>

          {showForgotPw && !forgotSent && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                type="email"
                className="form-input"
                placeholder="Enter your email address"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-ghost"
                style={{ width: '100%' }}
                disabled={forgotLoading || !forgotEmail}
                onClick={async () => {
                  setForgotLoading(true);
                  try {
                    await fetch('/api/auth/forgot-password', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: forgotEmail }),
                    });
                    setForgotSent(true);
                  } catch { /* ignore */ } finally {
                    setForgotLoading(false);
                  }
                }}
              >
                {forgotLoading ? 'Sending…' : 'Send reset link'}
              </button>
            </div>
          )}

          {forgotSent && (
            <p style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>
              If that email exists, a reset link has been sent.
            </p>
          )}

          {sessionExpired && (
            <p style={{ marginTop: 16, fontSize: '0.8125rem', color: 'var(--text-muted)', textAlign: 'center' }}>
              Your session has expired. Please log in again.
            </p>
          )}

        </div>
      </div>

      {/* Right splash */}
      <div className="login-splash" style={{ flex: 1, minHeight: '100vh', background: '#070e1c', overflow: 'hidden' }}>
        <RightSplash />
      </div>

    </div>
  );
}
